import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Icon,
    Paper,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import {useMemo, useState} from "react";
import {useConsultarServicoGeral} from "../../api/DpgeIntegracaoEsfingeApi";
import useFeedback from "../../components/feedback/useFeedback";
import JsonPanel from "../../components/json-panel/JsonPanel";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import IconBadge from "../../components/icon-badge/IconBadge";

export const CONSULTAS = [
    {
        id: "registro",
        titulo: "Registro específico",
        descricao: "Consulte o processamento pelo código de registro.",
        icone: "fingerprint",
        campos: [
            {
                nome: "codigoRegistroInformacao",
                rotulo: "Código de registro",
                required: true
            }
        ]
    },
    {
        id: "registroGeral",
        titulo: "Visão geral do registro",
        descricao: "Recupere o histórico consolidado de um registro.",
        icone: "account_tree",
        campos: [
            {
                nome: "codigoRegistroInformacao",
                rotulo: "Código de registro",
                required: true
            }
        ]
    },
    {
        id: "lote",
        titulo: "Lotes assíncronos",
        descricao: "Consulte somente envios que geraram lote assíncrono.",
        icone: "inventory_2",
        campos: [
            {nome: "chavePacote", rotulo: "Chave do pacote", required: true}
        ]
    },
    {
        id: "periodo",
        titulo: "Movimentações por período",
        descricao: "Localize transmissões dentro de uma janela de tempo.",
        icone: "date_range",
        campos: [
            {
                nome: "dataInicio",
                rotulo: "Data inicial",
                tipo: "datetime-local",
                required: true
            },
            {
                nome: "dataFim",
                rotulo: "Data final",
                tipo: "datetime-local",
                required: true
            }
        ]
    },
    {
        id: "recebidos",
        titulo: "Registros recebidos",
        descricao: "Pesquise por chave de pacote ou código de registro.",
        icone: "download_done",
        campos: [
            {nome: "chavePacote", rotulo: "Chave do pacote"},
            {nome: "codigoRegistroInformacao", rotulo: "Código de registro"}
        ]
    }
];

const PERIODO_MAXIMO_EXCLUSIVO_MS = 30 * 24 * 60 * 60 * 1000;
const MODULO_NAO_IDENTIFICADO = "NAO_IDENTIFICADO";
const TIPOS_FOLHA = new Set([
    "ALTERACAO_POSSE",
    "ATUALIZACAO_CARGO",
    "ATUALIZACAO_CARGO_COMPLEMENTO",
    "COMPONENTE_FOLHA_PAGAMENTO",
    "CONCURSO",
    "EDITAL_CONCURSO",
    "FOLHA_PAGAMENTO",
    "INATIVOS",
    "INGRESSO_CONTRATO",
    "INGRESSO_ESTATUTARIO",
    "CONVOCACAO_CONCURSO",
    "PARECER_CI",
    "PENSIONISTAS",
    "PRORROGACAO_CONCURSO",
    "QUADRO_VAGAS",
    "SERVIDORES",
    "DADOS_FUNCIONAIS_ATIVOS"
]);
const TIPOS_FINANCEIROS = new Set(["CONTAS_BANCARIAS", "GESTAO_FISCAL"]);

export function validarPeriodoConsulta(valores) {
    if (!valores.dataInicio || !valores.dataFim) return "";

    const dataInicio = new Date(valores.dataInicio);
    const dataFim = new Date(valores.dataFim);

    if (Number.isNaN(dataInicio.getTime()) || Number.isNaN(dataFim.getTime())) {
        return "Informe datas válidas para realizar a consulta.";
    }

    if (dataFim < dataInicio) {
        return "A data final não pode ser anterior à data inicial.";
    }

    if (
        dataFim.getTime() - dataInicio.getTime() >=
        PERIODO_MAXIMO_EXCLUSIVO_MS
    ) {
        return "O e-Sfinge permite intervalos inferiores a 30 dias. Divida períodos maiores em consultas menores.";
    }

    return "";
}

export function montarConsulta(tipo, valores) {
    const limpos = Object.fromEntries(
        Object.entries(valores).filter(
            ([, valor]) => valor !== "" && valor != null
        )
    );

    if (tipo === "registro") {
        return {recurso: "porCodigoRegistro", parametros: limpos};
    }
    if (tipo === "registroGeral") {
        return {recurso: "porCodigoRegistroGeral", parametros: limpos};
    }
    if (tipo === "lote") {
        return {
            recurso: `statusLotePorChavePacote/${encodeURIComponent(
                valores.chavePacote
            )}`
        };
    }
    if (tipo === "periodo") {
        return {
            recurso: "porData",
            parametros: {
                dataInicio: new Date(valores.dataInicio).toISOString(),
                dataFim: new Date(valores.dataFim).toISOString()
            }
        };
    }
    return {recurso: "registrosRecebidos", parametros: limpos};
}

export function extrairIdentificadores(valor) {
    const codigosRegistro = new Set();
    const chavesPacote = new Set();
    const identificadoresPorModulo = new Map();
    const chavesCodigo = new Set([
        "codigoregistro",
        "codigosregistro",
        "codigoregistroinformacao",
        "codigosregistroinformacao"
    ]);
    const chavesPacoteAceitas = new Set(["chavepacote", "chavespacote"]);

    function adicionar(destino, conteudo, modulo, tipo) {
        const valores = Array.isArray(conteudo) ? conteudo : [conteudo];
        valores.forEach((item) => {
            if (
                ["string", "number"].includes(typeof item) &&
                `${item}`.trim()
            ) {
                const normalizado = `${item}`.trim();
                destino.add(normalizado);
                const grupo = obterGrupo(modulo);
                if (tipo === "codigo") {
                    grupo.codigosRegistro.add(normalizado);
                } else {
                    grupo.chavesPacote.add(normalizado);
                }
            }
        });
    }

    function obterGrupo(modulo) {
        const chave = modulo || MODULO_NAO_IDENTIFICADO;
        if (!identificadoresPorModulo.has(chave)) {
            identificadoresPorModulo.set(chave, {
                codigosRegistro: new Set(),
                chavesPacote: new Set()
            });
        }
        return identificadoresPorModulo.get(chave);
    }

    function percorrer(conteudo, moduloHerdado) {
        if (Array.isArray(conteudo)) {
            conteudo.forEach((item) => percorrer(item, moduloHerdado));
            return;
        }
        if (!conteudo || typeof conteudo !== "object") return;

        const moduloAtual =
            moduloPorTipoDadoEnviado(conteudo.tipoDadoEnviado) ||
            moduloHerdado ||
            MODULO_NAO_IDENTIFICADO;

        Object.entries(conteudo).forEach(([chave, item]) => {
            const chaveNormalizada = chave
                .toLowerCase()
                .replace(/[^a-z0-9]/g, "");
            if (chavesCodigo.has(chaveNormalizada)) {
                adicionar(codigosRegistro, item, moduloAtual, "codigo");
            }
            if (chavesPacoteAceitas.has(chaveNormalizada)) {
                adicionar(chavesPacote, item, moduloAtual, "pacote");
            }
            percorrer(item, moduloAtual);
        });
    }

    percorrer(valor);
    return {
        codigosRegistro: [...codigosRegistro],
        chavesPacote: [...chavesPacote],
        porModulo: Object.fromEntries(
            [...identificadoresPorModulo.entries()].map(([modulo, grupo]) => [
                modulo,
                {
                    codigosRegistro: [...grupo.codigosRegistro],
                    chavesPacote: [...grupo.chavesPacote]
                }
            ])
        )
    };
}

export function moduloPorTipoDadoEnviado(tipoDadoEnviado) {
    const normalizado = normalizarTipoDado(tipoDadoEnviado);
    if (!normalizado) return null;
    if (TIPOS_FOLHA.has(normalizado)) return "FOLHA";
    if (TIPOS_FINANCEIROS.has(normalizado)) return "FINANCEIRA";
    return null;
}

function normalizarTipoDado(tipoDadoEnviado) {
    if (!tipoDadoEnviado) return "";
    let tipo = `${tipoDadoEnviado}`
        .trim()
        .toUpperCase()
        .replaceAll("-", "_")
        .replaceAll(" ", "_");
    for (const sufixo of [
        "_ENVIAR_PARCIAL",
        "_FINALIZAR",
        "_INICIAR",
        "_PARCIAL",
        "_ENVIAR"
    ]) {
        if (tipo.endsWith(sufixo)) {
            tipo = tipo.slice(0, -sufixo.length);
            break;
        }
    }
    return tipo;
}

export function formatarIdentificadores(valores) {
    return valores.join(", ");
}

function ServicosGeraisPage() {
    const [tipo, setTipo] = useState("registro");
    const [valores, setValores] = useState({});
    const consulta = useConsultarServicoGeral();
    const definicao = useMemo(
        () => CONSULTAS.find((item) => item.id === tipo),
        [tipo]
    );
    const erroPeriodo =
        tipo === "periodo" ? validarPeriodoConsulta(valores) : "";

    const invalido =
        definicao.campos.some(
            (campo) => campo.required && !valores[campo.nome]
        ) ||
        Boolean(erroPeriodo) ||
        (tipo === "recebidos" &&
            !valores.chavePacote &&
            !valores.codigoRegistroInformacao);

    function selecionar(novoTipo) {
        setTipo(novoTipo);
        setValores({});
        consulta.reset();
    }

    function executar(event) {
        event.preventDefault();
        consulta.mutate(montarConsulta(tipo, valores));
    }

    return (
        <Page maxWidth="lg">
            <PageHeader
                primary="Serviços Gerais"
                secondary="Consulte registros, lotes assíncronos e movimentações do e-Sfinge."
            />
            <Typography variant="subtitle2" mb={1.5} color="text.secondary">
                Escolha o tipo de consulta
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(5, minmax(0, 1fr))"
                    },
                    gap: 2,
                    mb: 3
                }}
            >
                {CONSULTAS.map((item) => (
                    <Card
                        key={item.id}
                        variant="outlined"
                        sx={{
                            height: "100%",
                            minHeight: 150,
                            borderColor:
                                tipo === item.id ? "primary.main" : "divider",
                            borderWidth: tipo === item.id ? 2 : 1,
                            bgcolor:
                                tipo === item.id
                                    ? "action.hover"
                                    : "background.paper"
                        }}
                    >
                        <CardActionArea
                            onClick={() => selecionar(item.id)}
                            sx={{height: "100%", alignItems: "stretch"}}
                        >
                            <CardContent>
                                <IconBadge>{item.icone}</IconBadge>
                                <Typography fontWeight={700} mt={1}>
                                    {item.titulo}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    mt={0.5}
                                >
                                    {item.descricao}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>

            {definicao && (
                <Paper
                    component="form"
                    onSubmit={executar}
                    variant="outlined"
                    sx={{p: {xs: 2, md: 3}, mb: 3, borderRadius: 2}}
                >
                    <Typography variant="overline" color="primary">
                        Consulta selecionada
                    </Typography>
                    <Typography variant="h6" mb={2}>
                        {definicao.titulo}
                    </Typography>
                    {tipo === "lote" && (
                        <Alert severity="info" sx={{mb: 2}}>
                            Esta consulta é exclusiva para operações assíncronas
                            que retornam número de lote. Os fluxos de Atos de
                            Pessoal, inclusive Folha de Pagamento, usam envios
                            parciais síncronos; acompanhe a finalização pelo
                            código de registro.
                        </Alert>
                    )}
                    <Grid container spacing={2} alignItems="stretch">
                        {definicao.campos.map((campo) => (
                            <Grid
                                item
                                xs={12}
                                md={definicao.campos.length === 1 ? 8 : 4}
                                key={campo.nome}
                            >
                                <TextField
                                    fullWidth
                                    label={campo.rotulo}
                                    type={campo.tipo || "text"}
                                    required={campo.required}
                                    value={valores[campo.nome] || ""}
                                    onChange={(event) =>
                                        setValores((valoresAtuais) => ({
                                            ...valoresAtuais,
                                            [campo.nome]: event.target.value
                                        }))
                                    }
                                    InputLabelProps={
                                        campo.tipo ? {shrink: true} : undefined
                                    }
                                    error={
                                        campo.nome === "dataFim" &&
                                        Boolean(erroPeriodo)
                                    }
                                    helperText={
                                        campo.nome === "dataFim"
                                            ? erroPeriodo ||
                                              "O intervalo deve ser inferior a 30 dias."
                                            : undefined
                                    }
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={invalido || consulta.isLoading}
                                sx={{height: 56}}
                                startIcon={
                                    consulta.isLoading ? (
                                        <CircularProgress
                                            color="inherit"
                                            size={18}
                                        />
                                    ) : (
                                        <Icon>search</Icon>
                                    )
                                }
                            >
                                Consultar
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {consulta.isError && (
                <Alert severity="error" sx={{mb: 3}}>
                    {consulta.error?.message ||
                        "Não foi possível concluir a consulta. Verifique os filtros e tente novamente."}
                </Alert>
            )}
            {consulta.data && <ResultadoConsulta resultado={consulta.data} />}
        </Page>
    );
}

function ResultadoConsulta({resultado}) {
    const sucesso = resultado.estado === "PROCESSADO_COM_SUCESSO";
    const resposta = resultado.resposta ?? resultado;
    const dadosConsultados = resposta?.registros ?? resposta;
    const identificadores = extrairIdentificadores(dadosConsultados);
    return (
        <Stack spacing={2} mb={4}>
            <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2}}>
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    spacing={2}
                    alignItems={{sm: "center"}}
                >
                    <IconBadge color={sucesso ? "success" : "warning"}>
                        {sucesso ? "check_circle" : "schedule"}
                    </IconBadge>
                    <Box flex={1}>
                        <Typography variant="overline">
                            Resultado da consulta
                        </Typography>
                        <Typography variant="h6">
                            {(resultado.evento || "Consulta").replaceAll(
                                "_",
                                " "
                            )}
                        </Typography>
                    </Box>
                    <Chip
                        label={(resultado.estado || "SEM ESTADO").replaceAll(
                            "_",
                            " "
                        )}
                        color={sucesso ? "success" : "default"}
                    />
                </Stack>
            </Paper>
            <FiltroAcessoConsulta resposta={resposta} />
            <IdentificadoresConsulta identificadores={identificadores} />
            <JsonPanel
                primary="Dados retornados"
                secondary="Resposta integral recebida do e-Sfinge"
                src={resposta}
            />
        </Stack>
    );
}

function IdentificadoresConsulta({identificadores}) {
    const {showInfo, showError} = useFeedback();
    const {codigosRegistro, chavesPacote} = identificadores;
    const grupos = Object.entries(identificadores.porModulo || {}).filter(
        ([, grupo]) =>
            grupo.codigosRegistro.length > 0 || grupo.chavesPacote.length > 0
    );

    if (!codigosRegistro.length && !chavesPacote.length) return null;

    async function copiarTodos(valores, descricaoSingular, descricaoPlural) {
        try {
            await navigator.clipboard.writeText(
                formatarIdentificadores(valores)
            );
            const descricao =
                valores.length === 1 ? descricaoSingular : descricaoPlural;
            showInfo(
                `${valores.length} ${descricao} copiado${
                    valores.length === 1 ? "" : "s"
                }`
            );
        } catch {
            showError("Não foi possível copiar os identificadores.");
        }
    }

    return (
        <Paper variant="outlined" sx={{p: 2.5, borderRadius: 2}}>
            <Stack
                direction={{xs: "column", md: "row"}}
                spacing={2}
                alignItems={{md: "center"}}
            >
                <Box flex={1}>
                    <Typography variant="h6">
                        Identificadores encontrados
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Os valores são separados pelo módulo informado pelo
                        e-Sfinge. O cancelamento ainda valida a ocorrência de
                        origem no histórico local.
                    </Typography>
                </Box>
            </Stack>
            <Stack spacing={1.5} mt={2}>
                {grupos.map(([modulo, grupo]) => (
                    <Box
                        key={modulo}
                        sx={{
                            p: 2,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 2
                        }}
                    >
                        <Stack
                            direction={{xs: "column", md: "row"}}
                            spacing={1.5}
                            alignItems={{md: "center"}}
                        >
                            <Box flex={1}>
                                <Typography fontWeight={700}>
                                    {rotuloModulo(modulo)}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {orientacaoModulo(modulo)}
                                </Typography>
                            </Box>
                            {grupo.codigosRegistro.length > 0 && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Icon>content_copy</Icon>}
                                    onClick={() =>
                                        copiarTodos(
                                            grupo.codigosRegistro,
                                            "código de registro",
                                            "códigos de registro"
                                        )
                                    }
                                >
                                    Copiar códigos (
                                    {grupo.codigosRegistro.length})
                                </Button>
                            )}
                            {grupo.chavesPacote.length > 0 && (
                                <Button
                                    variant="outlined"
                                    startIcon={<Icon>content_copy</Icon>}
                                    onClick={() =>
                                        copiarTodos(
                                            grupo.chavesPacote,
                                            "chave de pacote",
                                            "chaves de pacote"
                                        )
                                    }
                                >
                                    Copiar chaves ({grupo.chavesPacote.length})
                                </Button>
                            )}
                        </Stack>
                    </Box>
                ))}
            </Stack>
        </Paper>
    );
}

function FiltroAcessoConsulta({resposta}) {
    if (
        !resposta ||
        typeof resposta !== "object" ||
        resposta.totalEncontrado == null
    ) {
        return null;
    }

    if (resposta.acessoIrrestrito) {
        return (
            <Alert severity="info">
                Visão integral habilitada para o perfil Master: todos os{" "}
                {resposta.totalExibido} registros encontrados estão visíveis.
            </Alert>
        );
    }

    const modulos = (resposta.modulosPermitidos || [])
        .map(rotuloModulo)
        .join(" e ");
    return (
        <Alert severity={resposta.totalOmitido > 0 ? "warning" : "info"}>
            Resultado filtrado para {modulos || "o perfil autenticado"}:{" "}
            {resposta.totalExibido} registro(s) visível(is)
            {resposta.totalOmitido > 0 &&
                ` e ${resposta.totalOmitido} omitido(s) por pertencerem a outro módulo ou não possuírem classificação reconhecida.`}
        </Alert>
    );
}

function rotuloModulo(modulo) {
    if (modulo === "FOLHA") return "Atos de Pessoal";
    if (modulo === "FINANCEIRA") return "Financeiro";
    return "Origem não identificada";
}

function orientacaoModulo(modulo) {
    if (modulo === "FOLHA") {
        return "Pode ser levado à Integração da Folha; a origem local será validada antes do preparo.";
    }
    if (modulo === "FINANCEIRA") {
        return "Utilize o fluxo financeiro ou o cancelamento contextual da ocorrência correspondente.";
    }
    return "Disponível somente para consulta. O cancelamento em lote permanece bloqueado sem origem comprovada.";
}

export default ServicosGeraisPage;
